import { UNRESTRICTED_ACTOR } from '../core/policy/actor';
import {
  type BuySellOrderField,
  buySellOrderPolicy,
  type BuySellOrderStatus as PolicyStatus,
} from '../core/policy/entities/buySellOrder';

/**
 * LEGACY ADAPTER over the Buy-Sell Order policy (`shared/src/core/policy/entities/buySellOrder.ts`).
 *
 *   Validator.isFieldEditable(field, status, n) ──▶ buySellOrderPolicy.decide(edit field)
 *   Validator.isRevertable(status)              ──▶ buySellOrderPolicy.decide(perform revert)
 *
 * No rule lives here any more. It stays for callers outside the BSO page (bill page charges,
 * the grid drill harness, the product repo's `active` list); new code asks the policy.
 */

// The enum-less 'ERROR' is kept for its callers' types (parked: it is not a BuySellOrderStatus).
type BuySellOrderStatus = PolicyStatus | 'ERROR';

// List of fields that can be editable in a BuySellOrder.
type EditableFields = Exclude<BuySellOrderField, 'orderDate'>;

/** 'ERROR' sits outside every window, exactly like CANCELLED. */
const toPolicyStatus = (status: BuySellOrderStatus): PolicyStatus => (status === 'ERROR' ? 'CANCELLED' : status);

// Validator class to determine field editability based on the BuySellOrder status.
export class Validator {
  public static active: BuySellOrderStatus[] = ['SUBMITTED', 'PAYMENT_RECONCILIATION'];

  public static isRevertable(status: BuySellOrderStatus) {
    return buySellOrderPolicy.decide(
      UNRESTRICTED_ACTOR,
      { status: toPolicyStatus(status) },
      { type: 'perform', op: 'revert' }
    ).allowed;
  }

  /**
   * Determines if a field is editable based on the current status of the BuySellOrder.
   * @param field The field to check for editability.
   * @param status The current status of the BuySellOrder.
   * @param numOpenInvoices? The number of open invoices for the BuySellOrder.
   * @returns Boolean indicating if the field is editable.
   */
  public static isFieldEditable(field: EditableFields, status: BuySellOrderStatus, numOpenInvoices = 0) {
    // Legacy arm: this table also closed `expenses` on an open invoice. No caller passes a count
    // for `expenses`, and the policy does not carry the arm; it is answered here, unchanged.
    if (field === 'expenses' && numOpenInvoices > 0) return false;

    return buySellOrderPolicy.decide(
      UNRESTRICTED_ACTOR,
      { status: toPolicyStatus(status), hasOpenInvoice: numOpenInvoices > 0 },
      { type: 'edit', field }
    ).allowed;
  }
}
